// The light burst of a reveal: a flash, two shock rings and a spray of particles,
// all plain DOM with CSS keyframes. It plays once, on mount.
import React, { useMemo } from "react";
import { makeParticles } from "./timeline";

export interface RevealBurstProps {
  /** Seeds the particle layout (use the copy id). */
  seed: string;
  count: number;
  /** Typical particle travel in px. */
  spread: number;
  colors: string[];
  /** Colour of the shock rings. */
  ringColor: string;
  /** The cinematic also gets the full-frame flash. */
  flash?: boolean;
  /** "gold": the second burst of a first-on-the-site cinematic (a gold flash). */
  variant?: "gold";
}

const RevealBurst: React.FC<RevealBurstProps> = ({ seed, count, spread, colors, ringColor, flash = false, variant }) => {
  const particles = useMemo(() => makeParticles(seed, count, spread, colors), [seed, count, spread, colors]);
  return (
    <div
      className={`bdg-burst${variant ? ` bdg-burst--${variant}` : ""}`}
      aria-hidden="true"
      data-testid={variant ? `badge-reveal-burst-${variant}` : "badge-reveal-burst"}
    >
      {flash && <div className="bdg-burst__flash" />}
      <div className="bdg-burst__ring" style={{ "--ring": ringColor } as React.CSSProperties} />
      <div className="bdg-burst__ring bdg-burst__ring--late" style={{ "--ring": ringColor } as React.CSSProperties} />
      {particles.map((p, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={`bdg-burst__p bdg-burst__p--${p.shape}`}
          style={
            {
              "--dx": `${p.dx}px`,
              "--dy": `${p.dy}px`,
              "--fall": `${p.fall}px`,
              "--s": `${p.size}px`,
              "--r": `${p.rotate}deg`,
              "--c": p.color,
              animationDelay: `${p.delay}ms`,
              animationDuration: `${p.duration}ms`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};

export default RevealBurst;
